'use client';

import React, { FC, PropsWithChildren, useEffect } from 'react';
import Link from 'next/link';
import logoutObserver from '@/observers/LogoutObserver';
import { useGetUser } from '@/queries/user';
import { useAPI } from '@/services/utils';
import Toast from '@/components/Toast';
import { toLowerCaseKeys } from '@/utils/object';
import {
  type MessagePayload,
  onForegroundMessage,
} from './utils/messaging/foreground';
import { getRouteByData } from './utils/route';
import { deleteFCMTokenData, maybeCreateFCMToken } from './utils/token';

const FirebaseContainer: FC<PropsWithChildren> = ({ children }) => {
  const { data } = useGetUser({ notifyOnChangeProps: ['data'] });
  const userKey = data?.pk || '';

  const { mutateAsync } = useAPI(['user', 'submitFireBaseToken'], {
    isMutation: true,
  });

  const handleSubmitToBackend = async (fcmToken: string) => {
    return mutateAsync({ FireBaseToken: fcmToken });
  };

  const handleFCMToken = async (onDone: Function) => {
    try {
      await maybeCreateFCMToken(userKey, handleSubmitToBackend);
      onDone();
    } catch (error: any) {
      if (error?.displayMode === 'Toast') {
        Toast.open({ variant: 'error', children: `${error?.message}` });
        onDone();
      } else {
        console.error('Firebase@HandleFCMToken@Error', error);
      }
    }
  };

  const handleForegroundMessage = (payload: MessagePayload) => {
    console.log('Firebase@Foreground@App:', payload);
    const { title, body } = payload.notification || {};

    const notificationData = toLowerCaseKeys(payload?.data);

    const redirectId = notificationData?.redirectid;
    const redirectData = notificationData?.redirectdata;

    if (title && body) {
      Toast.open({
        variant: 'info',
        title,
        children: redirectId ? (
          <Link href={getRouteByData(redirectId, redirectData)}>{body}</Link>
        ) : (
          body
        ),
        duration: undefined,
      });
    }
  };

  const handleWindowClick = () => {
    let isProcessing = false;
    const listener = async () => {
      if (isProcessing) return;
      isProcessing = true;
      return handleFCMToken(removeListener).finally(() => {
        isProcessing = false;
      });
    };
    window.addEventListener('click', listener);
    const removeListener = () => window.removeEventListener('click', listener);
    return removeListener;
  };

  useEffect(() => {
    if (!userKey) return;

    // Browsers need to user has interaction with UI
    const removeWindowListener = handleWindowClick();

    const unsubscribeForegroundMessage = onForegroundMessage(
      handleForegroundMessage
    );

    const unsubscribeLogout = logoutObserver.subscribe(deleteFCMTokenData);

    return () => {
      unsubscribeLogout();
      unsubscribeForegroundMessage();
      removeWindowListener();
    };
  }, [userKey]);

  return <>{children}</>;
};

export default FirebaseContainer;
